import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ref as dbRef, push, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import './VoiceMemos.css';

const VoiceMemos = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  // Recording states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Post-recording states
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [memoTitle, setMemoTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Saved memos
  const [savedMemos, setSavedMemos] = useState([]);
  const [selectedMemo, setSelectedMemo] = useState(null);
  const [isPlayingMemo, setIsPlayingMemo] = useState(false);

  // Speech bubble
  const [speechBubbleMessage, setSpeechBubbleMessage] = useState('');
  const [bubblePosition, setBubblePosition] = useState({ top: '15%', left: '10%' });
  const [bubbleSide, setBubbleSide] = useState('left'); // 'left' or 'right'

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(new Audio());
  const memoAudioRef = useRef(new Audio());

  const MAX_DURATION = 60;

  // Possible bubble positions
  const bubblePositions = [
    { top: '12%', left: '5%' },
    { top: '15%', right: '5%' },
    { top: '50%', left: '3%' },
    { top: '50%', right: '3%' },
    { bottom: '25%', left: '5%' },
    { bottom: '25%', right: '5%' },
  ];

  // Load speech bubble messages from Firebase
  useEffect(() => {
    const messagesRef = dbRef(database, 'config/voiceMemoMessages');
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const data = snapshot.val();
      if (data && Array.isArray(data) && data.length > 0) {
        const randomMessage = data[Math.floor(Math.random() * data.length)];
        setSpeechBubbleMessage(randomMessage);
      } else if (data && typeof data === 'object') {
        const messagesArray = Object.values(data);
        if (messagesArray.length > 0) {
          const randomMessage = messagesArray[Math.floor(Math.random() * messagesArray.length)];
          setSpeechBubbleMessage(randomMessage);
        }
      }
      // Set random position
      const randomPosition = bubblePositions[Math.floor(Math.random() * bubblePositions.length)];
      setBubblePosition(randomPosition);
      // Determine which side the bubble is on (left side has 'left' property, right side has 'right' property)
      setBubbleSide(randomPosition.left ? 'left' : 'right');
    });
    return () => unsubscribe();
  }, []);

  // Load saved memos from Firebase
  useEffect(() => {
    const memosRef = dbRef(database, 'voiceMemos');
    const unsubscribe = onValue(memosRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const memosList = Object.entries(data).map(([id, memo]) => ({
          id,
          ...memo
        }));
        memosList.sort((a, b) => b.timestamp - a.timestamp);
        setSavedMemos(memosList);
      } else {
        setSavedMemos([]);
      }
    });
    return () => unsubscribe();
  }, []);

  // Cleanup
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
      if (memoAudioRef.current) {
        memoAudioRef.current.pause();
        memoAudioRef.current.src = '';
      }
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  // Recording timer
  useEffect(() => {
    if (isRecording) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_DURATION - 1) {
            stopRecording();
            return MAX_DURATION;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isRecording]);

  // Convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setShowSaveDialog(true);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      setAudioBlob(null);
      setAudioUrl(null);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Could not access microphone. Please ensure you have granted permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const playRecording = () => {
    if (audioUrl && !isPlaying) {
      audioRef.current.src = audioUrl;
      audioRef.current.play();
      setIsPlaying(true);
      audioRef.current.onended = () => setIsPlaying(false);
    } else if (isPlaying) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const reRecord = () => {
    setShowSaveDialog(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setMemoTitle('');
    setRecordingTime(0);
  };

  const saveMemo = async () => {
    if (!audioBlob || !memoTitle.trim()) return;
    setIsSaving(true);
    try {
      const base64Audio = await blobToBase64(audioBlob);
      const memosRef = dbRef(database, 'voiceMemos');
      await push(memosRef, {
        title: memoTitle.trim(),
        audioData: base64Audio,
        duration: recordingTime,
        recordedBy: userProfile?.name || 'Unknown',
        timestamp: Date.now()
      });

      setShowSaveDialog(false);
      setAudioBlob(null);
      setAudioUrl(null);
      setMemoTitle('');
      setRecordingTime(0);
    } catch (error) {
      console.error('Error saving memo:', error);
      alert('Failed to save memo. Please try again.');
    }
    setIsSaving(false);
  };

  const playMemo = (memo) => {
    if (selectedMemo?.id === memo.id && isPlayingMemo) {
      memoAudioRef.current.pause();
      memoAudioRef.current.currentTime = 0;
      setIsPlayingMemo(false);
    } else {
      setSelectedMemo(memo);
      memoAudioRef.current.src = memo.audioData;
      memoAudioRef.current.play();
      setIsPlayingMemo(true);
      memoAudioRef.current.onended = () => setIsPlayingMemo(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="voice-memos-page">
      <motion.button
        className="back-button"
        onClick={() => navigate('/home')}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        ← Back
      </motion.button>

      {/* Main tape recorder */}
      <motion.div
        className="tape-recorder"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, type: "spring" }}
      >
        {/* Carrying straps */}
        <div className="strap left-strap"></div>
        <div className="strap right-strap"></div>

        {/* Wooden top with knobs */}
        <div className="recorder-top">
          <div className="wooden-knob left-knob">
            <div className="knob-top"></div>
          </div>
          <div className="recorder-label">Tape Recorder</div>
          <div className="wooden-knob right-knob">
            <div className="knob-top"></div>
            <div className="knob-marker"></div>
          </div>
        </div>

        {/* Main body */}
        <div className="recorder-body">
          {/* Pink front panel */}
          <div className="recorder-front">
            {/* Green display window */}
            <div className="display-window">
              {/* Circular speaker grille */}
              <div className="speaker-grille">
                <div className="speaker-center"></div>
                <div className="speaker-holes">
                  <div className="hole"></div>
                  <div className="hole"></div>
                  <div className="hole"></div>
                  <div className="hole"></div>
                  <div className="hole"></div>
                  <div className="hole"></div>
                  <div className="hole"></div>
                  <div className="hole"></div>
                </div>
              </div>

              {/* Small dial */}
              <div className="small-dial">
                <div className="dial-dot"></div>
              </div>
            </div>

            {/* Timer display */}
            <div className="timer-display">
              <span className={isRecording ? 'recording-text' : ''}>
                {formatTime(recordingTime)} / {formatTime(MAX_DURATION)}
              </span>
            </div>

            {/* Recording indicator */}
            <div className={`recording-indicator ${isRecording ? 'active' : ''}`}>
              <div className="indicator-light"></div>
              <span>REC</span>
            </div>
          </div>

          {/* Control buttons */}
          <div className="control-buttons">
            {/* Record button - red circle */}
            <motion.button
              className={`control-btn record-btn ${isRecording ? 'active' : ''}`}
              onClick={startRecording}
              disabled={isRecording || showSaveDialog}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <div className="btn-inner record-inner">
                <div className="record-circle"></div>
              </div>
            </motion.button>

            {/* Stop button - square */}
            <motion.button
              className="control-btn stop-btn"
              onClick={stopRecording}
              disabled={!isRecording}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <div className="btn-inner stop-inner">
                <div className="stop-square"></div>
              </div>
            </motion.button>

            {/* Play button - triangle */}
            <motion.button
              className={`control-btn play-btn ${isPlaying ? 'active' : ''}`}
              onClick={playRecording}
              disabled={!audioUrl || isRecording}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <div className="btn-inner play-inner">
                <div className="play-triangle"></div>
              </div>
            </motion.button>
          </div>
        </div>
      </motion.div>

      {/* Save dialog */}
      <AnimatePresence>
        {showSaveDialog && (
          <motion.div
            className="save-dialog-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="save-dialog"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
            >
              <h3>Save Your Recording</h3>
              <p className="duration-text">Duration: {formatTime(recordingTime)}</p>

              <div className="dialog-buttons-row">
                <motion.button
                  className="dialog-btn listen-btn"
                  onClick={playRecording}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isPlaying ? '⏹ Stop' : '▶ Listen'}
                </motion.button>

                <motion.button
                  className="dialog-btn rerecord-btn"
                  onClick={reRecord}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  ↺ Re-record
                </motion.button>
              </div>

              <input
                type="text"
                className="title-input"
                placeholder="Give your memo a title..."
                value={memoTitle}
                onChange={(e) => setMemoTitle(e.target.value)}
                maxLength={50}
              />

              <div className="dialog-buttons-row">
                <motion.button
                  className="dialog-btn cancel-btn"
                  onClick={reRecord}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Cancel
                </motion.button>

                <motion.button
                  className="dialog-btn save-btn"
                  onClick={saveMemo}
                  disabled={!memoTitle.trim() || isSaving}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isSaving ? 'Saving...' : '💾 Save'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Saved memos list */}
      <motion.div
        className="saved-memos-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <h3>Saved Recordings</h3>

        {savedMemos.length === 0 ? (
          <p className="no-memos">No voice memos yet. Record your first one!</p>
        ) : (
          <div className="memos-list">
            {savedMemos.map((memo) => (
              <motion.div
                key={memo.id}
                className={`memo-item ${selectedMemo?.id === memo.id ? 'selected' : ''}`}
                onClick={() => setSelectedMemo(memo)}
                whileHover={{ scale: 1.02 }}
              >
                <div className="memo-info">
                  <span className="memo-title">{memo.title}</span>
                  <span className="memo-meta">
                    {formatTime(memo.duration)} • {memo.recordedBy}
                  </span>
                </div>

                <div className="memo-actions">
                  <motion.button
                    className={`memo-play-btn ${selectedMemo?.id === memo.id && isPlayingMemo ? 'playing' : ''}`}
                    onClick={(e) => { e.stopPropagation(); playMemo(memo); }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {selectedMemo?.id === memo.id && isPlayingMemo ? '⏹' : '▶'}
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default VoiceMemos;
